import React, { useEffect, useState } from "react";
import { Button, Table, Row, Col, Card, Typography, Form, Input, Select, message } from "antd";
import { fetchData, showError } from "../../utils";
import { useBlockScreen, useRoot } from "../../rootContext";
import { EyeOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import OrderDetailsModal from "./OrderDetailsModal";


const ProductsBestBuy = () => {
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
    const [filterData, setFilterData] = useState({
        skus: '',
        platforms: [],
        brands: [],
        status: undefined
    });
    const [selectOptions, setSelectOptions] = useState({ brands: [], platforms: [] });
    const controllerRef = React.useRef(new AbortController());
    const blocker = useBlockScreen();
    const root = useRoot();

    const [modalVisible, setModalVisible] = useState(false);
    const [orderDetails, setOrderDetails] = useState(null);
    const [form] = Form.useForm();

    // Load products from ProductsBestBuy API
    const loadOrders = async (page = 1, pageSize = 20) => {
        if (loading) controllerRef.current.abort();
        controllerRef.current = new AbortController();
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                p_Page: page.toString(),
                p_PageSize: pageSize.toString(),
                p_OrderId: filterData.orderId || '',
                p_OrderState: filterData.status || '',
                p_CustomerName: filterData.customerName || ''
            });

            const resp = await fetchData(
                `${window.SiteBasePath}/api/Products/ProductsBestBuy?${queryParams}`,
                { blocker, root }
            );

            if (resp?.Data?.Orders) {
                console.log(resp?.Data?.Orders);
                setProducts(resp.Data.Orders);
                setTotalCount(resp.Data.Orders.length); // backend can provide total count if needed
            } else {
                setProducts([]);
                setTotalCount(0);
            }
        } catch (err) {
            showError("Failed to fetch orders");
        } finally {
            setLoading(false);
        }
    };

    const handleView = async (record) => {
        try {
            setLoading(true);
            console.log(record);
            // Use the OrderId from the clicked record
            const resp = await fetchData(
                `${window.SiteBasePath}/api/Products/GetOrderItems/${record.OrderId}`,
                { blocker, root }
            );
            console.log(resp);
            // Check if Orders array exists
            if (resp?.Data?.Orders?.length) {
                setOrderDetails(resp.Data.Orders[0]); // set the first order
                setModalVisible(true); // show modal
            } else {
                message.warning("No details found for this order");
            }
        } catch (err) {
            showError("Failed to fetch order details");
        } finally {
            setLoading(false);
        }
    };

    const fetchOrderDetails = async (orderId) => {
        setLoading(true);
        try {
            const resp = await fetchData(
                `${window.SiteBasePath}/api/Products/GetOrderItems?orderId=${orderId}`,
                { blocker, root }
            );

            if (resp?.Data?.Items) {
                message.info(`Order ${orderId} has ${resp.Data.Items.length} items`);
                console.log(resp.Data.Items); // ya display in modal
            }
        } catch (err) {
            showError("Failed to fetch order items");
        } finally {
            setLoading(false);
        }
    };

    // Load master data for filters
    useEffect(() => {
        fetchData(`${window.SiteBasePath}/api/Products/master-data?types=platforms,brands`, { blocker, root })
            .then(resp => resp && setSelectOptions(resp));
    }, []);

    const didMountRef = React.useRef(false);

    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            loadOrders();
        }
    }, []);

    const columns = [
        { title: "Order ID", dataIndex: "OrderId", key: "OrderId" },
        { title: "Customer ID", dataIndex: "CustomerId", key: "CustomerId" },
        { title: "First Name", dataIndex: "FirstName", key: "FirstName" },
        { title: "Last Name", dataIndex: "LastName", key: "LastName" },
        { title: "Email", dataIndex: "Email", key: "Email" },
        { title: "Order State", dataIndex: "OrderState", key: "OrderState" },
        {
            title: "Shipping Deadline",
            dataIndex: "ShippingDeadline",
            key: "ShippingDeadline",
            render: (dateString) => {
                if (!dateString) return "-";
                const date = new Date(dateString);
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0'); 
                const year = date.getFullYear();
                return `${day}-${month}-${year}`;
            }
        },
        { title: "Item Count", dataIndex: "ItemCount", key: "ItemCount" },
        { title: "Total Amount", dataIndex: "TotalAmount", key: "TotalAmount", render: (amt) => `$${amt.toFixed(2)}` },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                        type="primary"
                        icon={<EyeOutlined />}
                        size="small"
                        onClick={() => handleView(record)}
                    >
                        View
                    </Button>
                    
                    <Button
                        type="danger"
                        icon={<DeleteOutlined />}
                        size="small"
                        onClick={() => handleView(record)}
                    >
                        Delete
                    </Button>
                </div>
            ),
        }
    ];

    return (
        <Card style={{ padding: 16 }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
                <Col>
                    <Typography.Title level={4} style={{ margin: 0 }}>Products BestBuy</Typography.Title>
                    <Typography.Text type="secondary">Manage BestBuy products — view details</Typography.Text>
                </Col>
            </Row>

            {/* Filter Section */}
            <Card style={{ marginBottom: "10px" }}>
                <Form form={form} layout="vertical">
                    <Row gutter={16}>
                        <Col span={6}>
                            <Form.Item label="SKUs">
                                <Input
                                    value={filterData.skus}
                                    placeholder="0000,1111,..."
                                    onChange={(e) => setFilterData(prev => ({ ...prev, skus: e.target.value }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Platforms">
                                <Select
                                    mode="multiple"
                                    value={filterData.platforms}
                                    onChange={(val) => setFilterData(prev => ({ ...prev, platforms: val }))}
                                    options={(selectOptions.platforms || []).map(p => ({ value: p.Id, label: p.Name }))}
                                    placeholder="Select Platforms..."
                                    maxTagCount="responsive"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Brands">
                                <Select
                                    mode="multiple"
                                    value={filterData.brands}
                                    onChange={(val) => setFilterData(prev => ({ ...prev, brands: val }))}
                                    options={(selectOptions.brands || []).map(b => ({ value: b, label: b }))}
                                    placeholder="Select Brands..."
                                    maxTagCount="responsive"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="Status">
                                <Select
                                    value={filterData.status}
                                    onChange={(val) => setFilterData(prev => ({ ...prev, status: val }))}
                                    options={[
                                        { value: undefined, label: "All" },
                                        { value: true, label: "Active" },
                                        { value: false, label: "Inactive" }
                                    ]}
                                    placeholder="Select Status"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row justify="end" style={{ marginTop: 10 }}>
                        <Button type="default" onClick={() => loadOrders(1, pagination.pageSize)}>Search</Button>
                        <Button type="link" onClick={() => {
                            setFilterData({ skus: '', platforms: [], brands: [], status: undefined });
                            loadOrders(1, pagination.pageSize);
                        }}>Reset</Button>
                    </Row>
                </Form>
            </Card>

            {/* Table */}
            <Table
                dataSource={products}
                columns={columns}
                rowKey={record => record.ORDER_ID}
                pagination={{
                    total: totalCount,
                    pageSize: pagination.pageSize,
                    current: pagination.current,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        setPagination({ current: page, pageSize });
                        loadOrders(page, pageSize);
                    }
                }}
                scroll={{ x: true }}
                loading={loading}
            />
            <OrderDetailsModal
                visible={modalVisible}
                onClose={() => setModalVisible(false)}
                orderDetails={orderDetails}
            />
        </Card>
    );
};

export default ProductsBestBuy;